/* eslint-disable max-len */
import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { customRange } from '@next/arable-lib/shared';

import { CoreActions } from '@next/core-lib';
import {
  AbstractDialogResultData,
  DIALOG_RETURN_TYPES,
  DialogService,
} from '@next/core-lib/dialog';
import { EnvironmentInjector } from '@next/core-lib/environment';

import { LocaleService } from '@next/core-lib/i18n';
import { SerializableObject, SessionActions, SessionSelectors } from '@next/core-lib/session';
import { SiteState } from '@next/core-lib/site';
import { UserActions } from '@next/core-lib/user';
import { WindowService, WindowServiceInjector } from '@next/core-lib/window';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, Observable, forkJoin, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  filter,
  finalize,
  map,
  mergeMap,
  startWith,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs/operators';

import * as ManureSpaceActions from 'src/app/manure-space/store/manure-space.actions';

import { Environment } from 'src/environments/environment';

import * as AppActionTypes from './actions';
import { SetReportManureSubstancesView, SetReportSort } from './actions';
import {
  getAvailableCropYears,
  getCropYear,
  getDefaultCropYear,
  getReportSort,
  selectCustomerProgress,
  selectSessionCustomerId,
} from './selectors';
import * as FertilizationLegalStandardsActions from '../fertilization-legal-standards/store/fertilization-legal-standard.actions';
import { AnimalManureTransportsTableComponent } from '../fertilizers/fertilizers-overview/animal-manure-transport/animal-manure-transports-table/animal-manure-transports-table.component';
import {
  FetchAnimalManureTransportsDialogComponent,
  FetchAnimalManureTransportsRequestParams,
} from '../fertilizers/fertilizers-overview/animal-manure-transport/fetch-animal-manure-transports-dialog/fetch-animal-manure-transports-dialog.component';
import { SewageSludgeCompostImportDialogComponent } from '../fertilizers/fertilizers-overview/sewage-sludge-compost-transport/import-dialog/sewage-sludge-compost-import-dialog.component';
import { SewageSludgeCompostTableComponent } from '../fertilizers/fertilizers-overview/sewage-sludge-compost-transport/table/sewage-sludge-compost-table.component';
import { ImportSetupDialogComponent } from '../fertilizers/import-setup-dialog/import-setup-dialog.component';
import { GetAnimalManureTransportResponse } from '../fertilizers/models/animal-manure-transport.model';
import * as FertilizersActions from '../fertilizers/store/actions';
import { getCropFields } from '../fields/store/selectors';
import { ManureSpaceApiService } from '../manure-space/services/manure-space-api.service';
import { selectActiveTabManureSpace } from '../manure-space/store/manure-space.selectors';
import { ReportManureSubstancesView, ReportSort, ReportSortOption } from '../reports/models';
import { SalesOrderPeriodApiService } from '../sales-order-period/services/sales-order-period-api.service';
import { GetSoilAnalysesRequestData, SoilAnalysisGroups } from '../shared/models';
import { AnimalManureTransportService } from '../shared/services/animal-manure-transport.service';
import { ArableCustomerService } from '../shared/services/arable-customer.service';
import { CropVarietyService } from '../shared/services/crop-variety.service';
import { CropService } from '../shared/services/crop.service';
import { SoilAnalysisApiService } from '../shared/services/soil-analysis.service';
import * as FieldsActions from './../fields/store/actions';

@Injectable()
export class AppEffects {
  protected store = inject<Store<SiteState>>(Store);
  protected actions$ = inject(Actions);
  protected readonly dialog = inject(DialogService);
  private readonly soilAnalysisApiService = inject(SoilAnalysisApiService);
  private readonly cropService = inject(CropService);
  private readonly cropVarietyService = inject(CropVarietyService);
  private readonly periodService = inject(SalesOrderPeriodApiService);
  private readonly arableCustomerService = inject(ArableCustomerService);
  private readonly animalManureTransportService = inject(AnimalManureTransportService);
  private readonly locale = inject(LocaleService);
  private readonly manureSpaceApi = inject(ManureSpaceApiService);
  private readonly env = inject<Environment>(EnvironmentInjector);
  private readonly window = inject<WindowService>(WindowServiceInjector);
  private readonly platformID = inject<Object>(PLATFORM_ID);

  contextChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AppActionTypes.SetDefaultCropYear,
        UserActions.customerChanged,
        AppActionTypes.CropYearChanged,
      ),
      switchMap(() => of(AppActionTypes.ContextChanged())),
    ),
  );

  customerChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.customerChanged),
      withLatestFrom(this.store.select(selectSessionCustomerId)),
      switchMap(([_, customerId]) => [
        SetReportSort({ reportSort: this.loadReportSort(customerId) }),
        SetReportManureSubstancesView({
          reportManureSubstancesView: this.loadReportManureSubstancesView(customerId),
        }),
      ]),
    ),
  );

  resetManureData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.ContextChanged),
      map((_) => ManureSpaceActions.resetManureData()),
    ),
  );

  initialized$ = createEffect(
    () =>
      forkJoin([
        this.actions$.pipe(ofType(SessionActions.sessionInitialized), take(1)),
        this.actions$.pipe(ofType(UserActions.customerInitialized), take(1)),
      ]).pipe(
        filter(() => isPlatformBrowser(this.platformID)),
        withLatestFrom(
          this.store.select(selectSessionCustomerId),
          this.store.select(getDefaultCropYear),
        ),
        map(([_, customerId, cropYear]) => {
          if (customerId) {
            this.store.dispatch(SetReportSort({ reportSort: this.loadReportSort(customerId) }));
            this.store.dispatch(
              SetReportManureSubstancesView({
                reportManureSubstancesView: this.loadReportManureSubstancesView(customerId),
              }),
            );
          }
          if (!cropYear) {
            this.store.dispatch(AppActionTypes.GetDefaultCropYear());
          }
        }),
      ),
    { dispatch: false },
  );

  getDefaultCropYear$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.GetDefaultCropYear),
      switchMap(() =>
        this.periodService
          .getDefaultYear()
          .pipe(
            map((defaultYear: number) =>
              AppActionTypes.SetDefaultCropYear({ cropYear: defaultYear }),
            ),
          ),
      ),
    ),
  );

  setDefaultCropYear$ = createEffect(() => {
    const currentYear = new Date().getFullYear();
    const rangeStartYear = currentYear - 3;
    const rangeEndYear = currentYear + 1;
    const availableCropYears = customRange(rangeStartYear, rangeEndYear);
    return this.actions$.pipe(
      ofType(AppActionTypes.SetDefaultCropYear),
      switchMap(() => [
        AppActionTypes.SetAvailableCropYears({ availableCropYears }),
        AppActionTypes.LoadCropsRequest(),
        FertilizationLegalStandardsActions.getLoggedInEmployee(),
      ]),
    );
  });

  getDefaultSoilType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.GetDefaultSoilType),
      withLatestFrom(this.store.select(getDefaultCropYear)),
      switchMap(([_, cropYear]) =>
        this.arableCustomerService
          .getCompanySettingsDefaultSoilTypeId(cropYear)
          .pipe(map((soilTypeId: number) => AppActionTypes.SetDefaultSoilType({ soilTypeId }))),
      ),
    ),
  );

  upsertDefaultSoilType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.UpsertDefaultSoilType),
      switchMap((action) =>
        this.arableCustomerService
          .upsertDefaultSoilType(action.soilTypeId)
          .pipe(map(() => AppActionTypes.GetDefaultSoilType())),
      ),
    ),
  );

  loadCropFieldsRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.ContextChanged),
      map(() => FieldsActions.loadCropFieldsRequest()),
    ),
  );

  getSoilAnalysis$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.GetLatestSoilAnalysisRequest),
      withLatestFrom(this.store.select(getCropFields)),
      map(([_, cropFields]) => {
        const soilAnalysisGroups: SoilAnalysisGroups[] = cropFields.map((cropField) => ({
          id: cropField.id,
          soilAnalysisIds: cropField.soilAnalysisIds,
        }));
        const requestData: GetSoilAnalysesRequestData = {
          maxYears: 5,
          oneDesignationSuffices: true,
          designationNames: ['P-AL', 'P-PAE', 'Pw'],
          soilAnalysisGroups,
        };
        return requestData;
      }),
      switchMap((requestData) =>
        this.soilAnalysisApiService.getLatestSoilAnalysis(requestData).pipe(
          map((responseData) =>
            AppActionTypes.GetLatestSoilAnalysisSuccess({
              soilAnalysisGroups: responseData.soilAnalysisGroups,
            }),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadCrops$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.LoadCropsRequest),
      mergeMap(() =>
        this.cropService.getCrops().pipe(
          map((crops) => AppActionTypes.LoadCropsSuccess({ crops })),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadCropVarietiesByCropIds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.LoadCropVarietiesByCropIdsRequest),
      switchMap((action) =>
        action.cropIds.length > 0
          ? this.cropVarietyService.getCropVarietiesByCropIds(action.cropIds).pipe(
              map((cropVarieties) =>
                AppActionTypes.LoadCropVarietiesByCropIdsSuccess({
                  cropIds: action.cropIds,
                  cropVarieties,
                }),
              ),
              startWith(CoreActions.setLoading({ loading: true })),
              finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
            )
          : of(
              AppActionTypes.LoadCropVarietiesByCropIdsSuccess({ cropIds: [], cropVarieties: [] }),
            ),
      ),
    ),
  );

  setCropYear$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.SetCropYear),
      withLatestFrom(
        this.store.select(getCropYear),
        this.store.select(getAvailableCropYears),
        this.store.select(getDefaultCropYear),
      ),
      switchMap(([{ cropYear }, currentCropYear, availableCropYears, defaultCropYear]) => {
        if (isNaN(cropYear) || availableCropYears.indexOf(cropYear) === -1) {
          return EMPTY;
        }

        if (cropYear !== currentCropYear) {
          return of(
            SessionActions.setSession({
              data:
                cropYear === defaultCropYear
                  ? { arable: {} }
                  : {
                      arable: {
                        cropYear,
                      },
                    },
            }),
            AppActionTypes.CropYearChanged({ cropYear }),
          );
        } else {
          return EMPTY;
        }
      }),
    ),
  );

  navigateToFieldPlan$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActionTypes.NavigateToFieldPlan),
        withLatestFrom(this.store.select(SessionSelectors.selectSessionState)),
        map(([_, session]) => {
          this.store.dispatch(
            SessionActions.setSession({
              data: {
                ...session,
                arable: {
                  ...(session['arable'] as SerializableObject),
                  cropFieldType: 'ManureSpace',
                },
              },
            }),
          );
          this.store.dispatch(
            SessionActions.windowOpenWithSession({
              url: this.env.FIELD_PLAN_URL,
              queryParams: { cropFieldType: 'ManureSpace' },
              name: '_self',
            }),
          );
        }),
      ),
    { dispatch: false },
  );

  toggleReportSortOption$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.ToggleReportSortOption),
      withLatestFrom(this.store.select(getReportSort)),
      map(([_, reportSort]) => {
        const newReportSortOption =
          reportSort.option !== ReportSortOption.sortCropName
            ? ReportSortOption.sortCropName
            : ReportSortOption.sortCropFieldName;
        return SetReportSort({
          reportSort: { option: newReportSortOption, descending: reportSort.descending },
        });
      }),
    ),
  );

  toggleReportSortDirection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.ToggleReportSortDirection),
      withLatestFrom(this.store.select(getReportSort)),
      map(([_, reportSort]) =>
        SetReportSort({
          reportSort: {
            option: reportSort.option,
            descending: !reportSort.descending,
          },
        }),
      ),
    ),
  );

  saveReportSortOption$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActionTypes.SetReportSort),
        withLatestFrom(this.store.select(selectSessionCustomerId)),
        map(([{ reportSort }, customerId]) => this.saveReportSort(customerId, reportSort)),
      ),
    { dispatch: false },
  );

  saveReportManureSubstancesView$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActionTypes.SetReportManureSubstancesView),
        withLatestFrom(this.store.select(selectSessionCustomerId)),
        map(([{ reportManureSubstancesView }, customerId]) =>
          this.saveReportManureSubstancesView(customerId, reportManureSubstancesView),
        ),
      ),
    { dispatch: false },
  );

  loadCustomerProgress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.LoadCustomerProgress, AppActionTypes.ContextChanged),
      withLatestFrom(this.store.select(getCropYear)),
      mergeMap(([_, cropYear]) =>
        this.arableCustomerService.getCustomerProgress(cropYear).pipe(
          map((customerProgress) =>
            AppActionTypes.LoadCustomerProgressSuccess({ customerProgress }),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  toggleCustomerProgressManure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.ToggleCustomerProgressManure),
      withLatestFrom(this.store.select(selectCustomerProgress)),
      mergeMap(([_, customerProgress]) =>
        this.arableCustomerService
          .upsertCustomerProgress({
            ...customerProgress,
            processes: {
              ...customerProgress.processes,
              manurespaceCalculationsCompleted:
                !customerProgress.processes.manurespaceCalculationsCompleted,
            },
          })
          .pipe(
            map(() => AppActionTypes.LoadCustomerProgress()),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          ),
      ),
    ),
  );

  startFertilizerImportDialogFlow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FertilizersActions.startFertilizerImportDialogFlow),
      switchMap(() =>
        this.dialog
          .dialog(ImportSetupDialogComponent, {
            confirmButton: true,
            confirmButtonText: this.locale.translate('apply'),
          })
          .pipe(
            map(({ status, result }) => {
              if (status === DIALOG_RETURN_TYPES.confirm) {
                return ManureSpaceActions.getMineralBalance({
                  importParams: {
                    deliveriesFrom: result.deliveriesFrom.toISOString(),
                    deliveriesUntil: result.deliveriesUntil.toISOString(),
                  },
                });
              }
              return AppActionTypes.CloseImportDialog();
            }),
          ),
      ),
    ),
  );

  fetchAnimalManureTransports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FertilizersActions.fetchAnimalManureTransports),
      switchMap(() =>
        this.dialog
          .dialog(FetchAnimalManureTransportsDialogComponent, {
            confirmButton: true,
            confirmButtonText: this.locale.translate('apply'),
          })
          .pipe(
            mergeMap((dialogResult) =>
              this.handleFetchAnimalManureTransportsDialogResult(dialogResult),
            ),
          ),
      ),
    ),
  );

  private handleFetchAnimalManureTransportsDialogResult(
    dialogResult: AbstractDialogResultData<FetchAnimalManureTransportsRequestParams>,
  ): Observable<Action> {
    if (dialogResult.status !== DIALOG_RETURN_TYPES.confirm || !dialogResult.result) {
      return of(AppActionTypes.CloseAnimalManureTransportDatepickerDialog());
    }

    return this.fetchAnimalManureTransportsAndOpenTable(dialogResult.result);
  }

  private fetchAnimalManureTransportsAndOpenTable(
    result: FetchAnimalManureTransportsRequestParams,
  ): Observable<Action> {
    let hasClearedLoadingOnError = false;

    return this.animalManureTransportService
      .getAnimalManureTransports(
        result.kvkNumber,
        this.currentIsoDateString(result.startDate),
        this.currentIsoDateString(result.endDate),
      )
      .pipe(
        mergeMap((animalManureTransports) =>
          this.createAnimalManureTransportActions(result, animalManureTransports),
        ),
        catchError((error: HttpErrorResponse) => {
          hasClearedLoadingOnError = true;
          this.store.dispatch(CoreActions.setLoading({ loading: false }));
          return this.notifyAnimalManureTransportImportError(error);
        }),
        startWith(CoreActions.setLoading({ loading: true })),
        finalize(() => {
          if (!hasClearedLoadingOnError) {
            this.store.dispatch(CoreActions.setLoading({ loading: false }));
          }
        }),
      );
  }

  private createAnimalManureTransportActions(
    result: FetchAnimalManureTransportsRequestParams,
    animalManureTransports: GetAnimalManureTransportResponse,
  ): Action[] {
    return [
      FertilizersActions.openAnimalManureTransportsTable({
        ...result,
        animalManureTransports,
      }),
    ];
  }

  private notifyAnimalManureTransportImportError(error: HttpErrorResponse): Observable<Action> {
    return this.dialog
      .notify({
        title: this.locale.translate('sharedTransport.table.errorImport'),
        text: this.generateGetAnimalManureTransportsErrorMessage(error),
      })
      .pipe(map(() => AppActionTypes.CloseAnimalManureTransportDatepickerDialog()));
  }

  openAnimalManureTransports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FertilizersActions.openAnimalManureTransportsTable),
      // Sometimes when opening this dialog it gets an instant cancel trigger. This was added to prevent the cancel trigger.
      debounceTime(100),
      switchMap((animalManureTransportTableRequestData) =>
        this.dialog
          .fullscreenDialog(
            AnimalManureTransportsTableComponent,
            {
              ...animalManureTransportTableRequestData,
              title: this.locale.translate('animalManureTransport.datepickerDialogLabel'),
              confirmButton: true,
              confirmButtonText: this.locale.translate('animalManureTransport.table.importVDM'),
            },
            {
              minWidth: '80vw',
            },
          )
          .pipe(
            map((result) => {
              if (result.status === DIALOG_RETURN_TYPES.confirm) {
                return AppActionTypes.CloseAnimalManureTransportOverview();
              }
              return AppActionTypes.CloseAnimalManureTransportOverview();
            }),
          ),
      ),
    ),
  );

  fetchVzcTransports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FertilizersActions.fetchVzcTransports),
      switchMap(() =>
        this.dialog
          .dialog(SewageSludgeCompostImportDialogComponent, {
            confirmButton: true,
            confirmButtonText: this.locale.translate('apply'),
          })
          .pipe(
            mergeMap(({ status, result }) => {
              if (status === DIALOG_RETURN_TYPES.confirm) {
                return [FertilizersActions.openVzcTransportsTable(result)];
              }
              return [AppActionTypes.CloseVzcTransportOverview()];
            }),
          ),
      ),
    ),
  );

  openVzcTransports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FertilizersActions.openVzcTransportsTable),
      // Sometimes when opening this dialog it gets an instant cancel trigger. This was added to prevent the cancel trigger.
      debounceTime(100),
      switchMap((vzcTransportTableRequestData) =>
        this.dialog
          .fullscreenDialog(
            SewageSludgeCompostTableComponent,
            {
              ...vzcTransportTableRequestData,
              title: this.locale.translate('sewageSludgeAndCompost.title'),
              confirmButton: true,
              confirmButtonText: this.locale.translate('sewageSludgeAndCompost.importVZC'),
            },
            {
              minWidth: '80vw',
            },
          )
          .pipe(
            map((result) => {
              if (result.status === DIALOG_RETURN_TYPES.confirm) {
                return AppActionTypes.CloseVzcTransportOverview();
              }
              return AppActionTypes.CloseVzcTransportOverview();
            }),
          ),
      ),
    ),
  );

  importMineralBalance$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FertilizersActions.importMineralBalance),
      withLatestFrom(this.store.select(selectActiveTabManureSpace)),
      switchMap(([{ fertilizers }, manureSpaceResource]) => {
        if (manureSpaceResource?.state !== 'loaded' || !manureSpaceResource.data) {
          return of();
        }

        const manureSpaceId = manureSpaceResource.data.id;

        return this.manureSpaceApi.postFertilizers(manureSpaceId, fertilizers).pipe(
          map(() => FertilizersActions.fertilizersUpdated()),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        );
      }),
    ),
  );

  private readonly currentIsoDateString = (date: string) =>
    new Date(+new Date(date) - new Date().getTimezoneOffset() * 60000).toISOString();

  private generateGetAnimalManureTransportsErrorMessage(error: HttpErrorResponse): string {
    if (error.status === 400) {
      switch (error.error) {
        case 'RELATION_NUMBER_NOT_FOUND':
          return this.locale.translate(
            'sharedTransport.table.joinDataRelationNumberNotFoundErrorMessage',
          );
        case 'MISSING_TRANSPORT_CERTIFICATIONS_MANDATE':
          return this.locale.translate(
            'sharedTransport.table.joinDataMissingTransportCertificationsMandateErrorMessage',
          );
        case 'NO_MANDATE':
        default:
          return this.locale.translate('sharedTransport.table.joinDataNoMandateErrorMessage');
      }
    }

    if (error.status === 404) {
      return this.locale.translate('sharedTransport.table.joinDataNoMandateErrorMessage');
    }

    return this.locale.translate('animalManureTransport.table.genericErrorMessage');
  }

  private loadReportSort = (customerId: number): ReportSort => {
    const content = this.window.getCookie(this.getReportSortOptionCookieName(customerId));
    if (!content) {
      return { option: ReportSortOption.sortCropFieldName, descending: false };
    }

    const parts = content.split(' ');

    const option =
      ReportSortOption[
        (parts[0] as keyof typeof ReportSortOption) ?? ReportSortOption.sortCropFieldName
      ];
    const descending = parts[1] === 'DESC';

    return { option, descending };
  };

  private loadReportManureSubstancesView = (customerId: number): ReportManureSubstancesView => {
    const content = this.window.getCookie(this.getReportManureSubstancesViewCookieName(customerId));
    const reportManureSubstancesViews = Object.values(ReportManureSubstancesView);

    return reportManureSubstancesViews.includes(content as ReportManureSubstancesView)
      ? (content as ReportManureSubstancesView)
      : ReportManureSubstancesView.manureDetails;
  };

  private saveReportSort(customerId: number, reportSort: ReportSort): void {
    if (reportSort?.option) {
      this.window.setCookie(
        this.getReportSortOptionCookieName(customerId),
        reportSort.option.toString() + (reportSort.descending ? ' DESC' : ''),
        365 * 24 * 60 * 60 * 1000,
      );
    }
  }

  private saveReportManureSubstancesView(
    customerId: number,
    reportManureSubstancesView: ReportManureSubstancesView,
  ): void {
    if (reportManureSubstancesView) {
      this.window.setCookie(
        this.getReportManureSubstancesViewCookieName(customerId),
        reportManureSubstancesView,
        365 * 24 * 60 * 60 * 1000,
      );
    }
  }

  private getReportSortOptionCookieName = (customerId: number): string =>
    `customer-${customerId}-report-sort-option}`;

  private getReportManureSubstancesViewCookieName = (customerId: number): string =>
    `customer-${customerId}-report-manure-substances-view}`;
}
